// HOW-SLOTS-WEB-AGENDA-TASKS (2026-07-09): the reschedule dependency banner. Mounts ABOVE the
// Game Plan card in TeeupPageContent.js. Derived from any task on the active TeeUp that carries a
// `consequence` (reschedule_by_T | reschedule_if_undone) and is still incomplete
// (selectActiveTeeupRescheduleTasks) -- clears the instant the task is completed (the selector
// filters on !completed_at, so a TASK_UPSERTED with a fresh completed_at drops it from the list
// on the next render, no separate "clear" action needed).
//
// RESILIENCE (plan 4c): gated on the SAME tasks-capability + lazy load as TasksSection -- if
// capabilities is off/unreachable, or the tasks fetch fails, this renders nothing (tasks state
// stays an empty array, selectActiveTeeupRescheduleTasks resolves to [], banner is absent). Never
// blocks or throws into the Game Plan card below it.
import { useCallback, useEffect, useState } from "react"

import { useDispatch, useSelector } from "react-redux"

import { loadTasks } from "@actions/tasksActions"
import { proposeReschedule } from "@utils/agendaTasksApi"
import {
  selectActiveTeeupId,
  selectActiveTeeupOrganizers,
  selectActiveTeeupState,
  selectUserId,
} from "@selectors/activeTeeup"
import { selectTasksCapable } from "@selectors/capabilities"
import { selectActiveTeeupRescheduleTasks } from "@selectors/tasks"
import { selectTeeupPeople } from "@selectors/teeups"
import { aliasId, unaliasId } from "@utils/devBackendShim"

// HOW-worker WU-D1 (defect #205, 2026-07-09): the prior copy claimed the TeeUp "will
// automatically reschedule" -- untrue, no automatic-reschedule mechanism exists. Both
// consequence variants now render the same truthful dependency-warning shape from the
// scenario (frames 13 / D2-Step-10): "Reschedule if <task> is not completed[ by <due>]."
// `due_label` is optional (due_expression/due_label lands with WU-S2-BE; renders when present,
// omits gracefully today).
//
// HOW-worker WU-S3 (scenario beat 21, 2026-07-09): when the task has an assignee, the scenario's
// exact line leads with the assignee -- "Reschedule if @<first name>'s task (<title>) is not
// completed[ by <due>]." The title stays in parens because the banner can list multiple tasks;
// the assignee-only phrasing is ambiguous with 2+ tasks (orchestrator-ruled compromise shape).
// Unassigned tasks keep the original title-led form unchanged.
const RESCHEDULE_ROW_PREFIX = "Reschedule if"

// First name = display name up to the first space (same convention as the rest of the tasks UI).
const firstName = (name: string) => (name || "").split(" ")[0]

// planner ruling R-2a 2026-07-09: banner title-cases the phrase to match frame 13; rows stay
// lowercase per R-2. Presentation-layer transform only -- never applied to dueLabel()/
// AgendaTasksSections or any API/enum value. "day before" -> "Day Before"; "2h before" ->
// "2h Before" (acceptable per the word-initial rule); "Jul 9" unchanged.
const titleCase = (s: string) => s.replace(/\b[a-z]/g, c => c.toUpperCase())

export const RescheduleBanner = () => {
  const dispatch = useDispatch<any>()
  const capable = useSelector(selectTasksCapable)
  const teeupId = useSelector(selectActiveTeeupId)
  const rescheduleTasks = useSelector(selectActiveTeeupRescheduleTasks)
  const organizers = useSelector(selectActiveTeeupOrganizers)
  const activeTeeup = useSelector(selectActiveTeeupState)
  const userId = useSelector(selectUserId)

  // Compose isOrganiser mirroring teeups.js:276-278.
  const isOrganiser =
    activeTeeup.createdby === userId ||
    organizers.some(({ id }) => id === userId)

  // Track which tasks have had a reschedule proposal sent (swap to "Proposed" state).
  const [proposed, setProposed] = useState<Set<string>>(new Set())

  const handleProposeReschedule = useCallback(
    async (taskId: string) => {
      if (!teeupId) return
      // teeupId from the route/selector is the shim-ALIASED id; the API needs the raw CO uuid
      // (same unaliasId convention the actions layer applies in loadTasks/addTask).
      const result = await proposeReschedule(unaliasId(teeupId), taskId)
      // Only swap to "Proposed" on success; on ConflictError (no decided When) leave the
      // affordance so the user can try again later.
      if (result) {
        setProposed(prev => new Set(prev).add(taskId))
      }
    },
    [teeupId]
  )

  // Check whether a task is overdue: due_at in the past and not yet completed.
  const isOverdue = (t: (typeof rescheduleTasks)[number]) => {
    if (!t.due_at || t.completed_at) return false
    return new Date(t.due_at) < new Date()
  }
  const participants = useSelector(selectTeeupPeople)

  // Piggybacks on the same tasks list TasksSection loads (loadTasks is idempotent per teeupId --
  // both effects fire the same GET; harmless if TasksSection also loads it, and this banner must
  // not assume TasksSection is mounted/ordered before it).
  useEffect(() => {
    if (capable && teeupId) dispatch(loadTasks(teeupId))
  }, [capable, teeupId, dispatch])

  if (!capable || rescheduleTasks.length === 0) return null

  return (
    <div
      data-testid="teeup-detail-reschedule-banner"
      className="mb-3 flex items-start gap-2.5 rounded-[13px] border border-[#F0D3C4] bg-[#FBEFEA] px-4 py-3">
      <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#E9B79B] text-[11px] font-bold text-white">
        !
      </span>
      <div className="flex-1 text-[13px] text-[#8A4B2D]">
        {rescheduleTasks.map(t => {
          const overdue = isOverdue(t)
          const hasProposed = proposed.has(t.id)
          const assigneeName = t.assignee_id
            ? participants[aliasId(t.assignee_id)]?.name || ""
            : ""
          const assigneeFirst = firstName(assigneeName)
          return (
            <div key={t.id} data-testid="reschedule-banner-row" className="py-0.5 flex items-center gap-2">
              <span>
                {assigneeFirst ? (
                  <>
                    {RESCHEDULE_ROW_PREFIX}{" "}
                    <span data-testid="reschedule-banner-assignee" className="font-semibold">
                      @{assigneeFirst}
                    </span>
                    's task (<span className="font-semibold">{t.title}</span>) is not
                    completed{t.due_label ? ` by ${titleCase(t.due_label)}` : ""}.
                  </>
                ) : (
                  <>
                    {RESCHEDULE_ROW_PREFIX} <span className="font-semibold">{t.title}</span> is not
                    completed{t.due_label ? ` by ${titleCase(t.due_label)}` : ""}.
                  </>
                )}
              </span>
              {isOrganiser && overdue && !hasProposed && (
                <button
                  data-testid="reschedule-banner-propose"
                  className="ml-1 inline-flex items-center rounded-[6px] border border-[#D4A574] bg-white px-2.5 py-0.5 text-[12px] font-medium text-[#8A4B2D] hover:bg-[#FBEFEA] active:bg-[#F0D3C4]"
                  onClick={() => handleProposeReschedule(t.id)}>
                  Propose rescheduling
                </button>
              )}
              {isOrganiser && hasProposed && (
                <span className="ml-1 inline-flex items-center rounded-[6px] bg-[#F0D3C4] px-2.5 py-0.5 text-[12px] font-medium text-[#8A4B2D]">
                  Proposed
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default RescheduleBanner
